
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { LayoutGrid, Table } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TransactionTable } from "@/components/history/TransactionTable";
import { TransactionCharts } from "@/components/history/TransactionCharts";
import { useTransactions } from "@/components/history/hooks/useTransactions";
import { ErrorState } from "@/components/owner/ErrorState";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subMonths, startOfMonth, endOfMonth, startOfDay, endOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { Input } from "@/components/ui/input";
import { StatisticsCharts } from "@/components/history/charts/StatisticsCharts";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";

const History = () => {
  const [period, setPeriod] = useState("all");
  const [transactionType, setTransactionType] = useState("all");
  const [viewMode, setViewMode] = useState<"table" | "charts">("table");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showCharts, setShowCharts] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();

  const { data: transactions, isError, error } = useTransactions(transactionType, period, selectedDate);

  const { data: todayStats } = useQuery({
    queryKey: ["todayStats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const today = startOfDay(new Date());
      const todayEnd = endOfDay(new Date());
      
      const { data: validatedTransactions, error: validatedError } = await supabase
        .from("poultry_transactions")
        .select("total_amount, refund_amount, transaction_type")
        .eq("is_deducted_from_revenue", true)
        .eq("created_by", user.id)
        .gte("created_at", today.toISOString())
        .lte("created_at", todayEnd.toISOString());

      if (validatedError) throw validatedError;

      const { data: unvalidatedTransactions, error: unvalidatedError } = await supabase
        .from("poultry_transactions")
        .select("total_amount, refund_amount, transaction_type")
        .eq("is_deducted_from_revenue", false)
        .eq("created_by", user.id)
        .gte("created_at", today.toISOString())
        .lte("created_at", todayEnd.toISOString());

      if (unvalidatedError) throw unvalidatedError;

      const validatedAmount = validatedTransactions.reduce((sum, transaction) => {
        if (transaction.transaction_type === 'sale') {
          return sum + transaction.total_amount;
        } else if (transaction.transaction_type === 'return') {
          return sum - (transaction.refund_amount || 0);
        }
        return sum;
      }, 0);

      const unvalidatedAmount = unvalidatedTransactions.reduce((sum, transaction) => {
        if (transaction.transaction_type === 'sale') {
          return sum + transaction.total_amount;
        } else if (transaction.transaction_type === 'return') {
          return sum - (transaction.refund_amount || 0);
        }
        return sum;
      }, 0);

      return {
        validatedAmount,
        unvalidatedAmount,
        hasUnvalidated: unvalidatedTransactions.length > 0
      };
    },
    refetchInterval: 30000,
  });

  const { data: transactionDates } = useQuery({
    queryKey: ["transaction_dates"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("poultry_transactions")
        .select("created_at")
        .eq("created_by", user.id);

      if (error) throw error;

      return data.map(t => new Date(t.created_at).toISOString().split('T')[0]);
    },
  });

  const { data: validatedTotal } = useQuery({
    queryKey: ["current_month_validated_total"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Définir le début et la fin du mois en cours
      const currentMonthStart = startOfMonth(new Date());
      const currentMonthEnd = endOfMonth(new Date());

      const { data: transactions, error } = await supabase
        .from("poultry_transactions")
        .select("total_amount, refund_amount, transaction_type")
        .eq("is_deducted_from_revenue", true)
        .eq("created_by", user.id)
        .gte("created_at", currentMonthStart.toISOString())
        .lte("created_at", currentMonthEnd.toISOString());

      if (error) throw error;

      return transactions.reduce((sum, transaction) => {
        if (transaction.transaction_type === 'sale') {
          return sum + transaction.total_amount;
        } else if (transaction.transaction_type === 'return') {
          return sum - (transaction.refund_amount || 0);
        }
        return sum;
      }, 0);
    },
  });

  const { data: customPeriodTotal } = useQuery({
    queryKey: ["custom_period_total", startDate, endDate],
    enabled: !!startDate && !!endDate,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data: transactions, error } = await supabase
        .from("poultry_transactions")
        .select("total_amount, refund_amount, transaction_type")
        .eq("is_deducted_from_revenue", true)
        .eq("created_by", user.id)
        .gte("created_at", `${startDate}T00:00:00`)
        .lte("created_at", `${endDate}T23:59:59`);

      if (error) throw error;

      return transactions.reduce((sum, transaction) => {
        if (transaction.transaction_type === 'sale') {
          return sum + transaction.total_amount;
        } else if (transaction.transaction_type === 'return') {
          return sum - (transaction.refund_amount || 0);
        }
        return sum;
      }, 0);
    },
  });

  const { data: lastMonthStats } = useQuery({
    queryKey: ["last_month_stats"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Définir le début et la fin du mois dernier
      const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
      const lastMonthEnd = endOfMonth(subMonths(new Date(), 1));

      const { data: transactions, error } = await supabase
        .from("poultry_transactions")
        .select("total_amount, refund_amount, transaction_type")
        .eq("is_deducted_from_revenue", true)
        .eq("created_by", user.id)
        .gte("created_at", lastMonthStart.toISOString())
        .lte("created_at", lastMonthEnd.toISOString());

      if (error) throw error;

      return transactions.reduce((sum, transaction) => {
        if (transaction.transaction_type === 'sale') {
          return sum + transaction.total_amount;
        } else if (transaction.transaction_type === 'return') {
          return sum - (transaction.refund_amount || 0);
        }
        return sum;
      }, 0);
    },
  });

  const { data: yearlyTotal } = useQuery({
    queryKey: ["yearly_validated_total"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      // Définir le début et la fin de l'année en cours
      const currentYear = new Date().getFullYear();
      const startDate = new Date(currentYear, 0, 1);
      const endDate = new Date(currentYear, 11, 31, 23, 59, 59, 999);

      const { data: transactions, error } = await supabase
        .from("poultry_transactions")
        .select("total_amount, refund_amount, transaction_type")
        .eq("is_deducted_from_revenue", true)
        .eq("created_by", user.id)
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      if (error) throw error;

      return transactions.reduce((sum, transaction) => {
        if (transaction.transaction_type === 'sale') {
          return sum + transaction.total_amount;
        } else if (transaction.transaction_type === 'return') {
          return sum - (transaction.refund_amount || 0);
        }
        return sum;
      }, 0);
    },
  });

  const lastMonth = format(subMonths(new Date(), 1), 'MMMM yyyy', { locale: fr });

  const { data: currency } = useOwnerCurrency();

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString()} ${currency || 'GNF'}`;
  };

  if (isError) {
    console.error("Error fetching transactions:", error);
    return <ErrorState message="Une erreur s'est produite lors du chargement de l'historique." />;
  }

  return (
    <DashboardLayout userType="owner">
      <div className="space-y-6 p-0 md:p-4 max-w-full">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <h1 className="text-3xl font-bold">Historique des mouvements</h1>
          <div className="flex flex-wrap gap-4 w-full md:w-auto">
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === "table" ? "default" : "outline"}
                size="icon"
                onClick={() => setViewMode("table")}
                title="Vue tableau"
              >
                <Table className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "charts" ? "default" : "outline"}
                size="icon"
                onClick={() => setViewMode("charts")}
                title="Vue graphiques"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal w-[240px]",
                    !selectedDate && "text-muted-foreground"
                  )}
                >
                  {selectedDate ? (
                    format(selectedDate, "PPP", { locale: fr })
                  ) : (
                    "Sélectionner une date"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  modifiers={{ 
                    hasTransaction: (date) => 
                      transactionDates?.includes(date.toISOString().split('T')[0]) ?? false 
                  }}
                  modifiersStyles={{
                    hasTransaction: {
                      backgroundColor: "#0ea5e9",
                      color: "white",
                      borderRadius: "4px"
                    }
                  }}
                  className="rounded-md border"
                />
              </PopoverContent>
            </Popover>

            <Select value={transactionType} onValueChange={setTransactionType}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Type de mouvement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les mouvements</SelectItem>
                <SelectItem value="sale">Ventes</SelectItem>
                <SelectItem value="credit_sale">Ventes à crédit</SelectItem>
                <SelectItem value="restock">Réapprovisionnements</SelectItem>
                <SelectItem value="loss">Pertes</SelectItem>
                <SelectItem value="return">Retours</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <Card className="p-2 md:p-4 w-full">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="w-full">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium text-gray-600">
                      Chiffre d'affaires aujourd'hui
                    </div>
                    <div className="mt-2">
                      <div className="text-xl font-bold text-green-600">
                        {formatCurrency(todayStats?.validatedAmount || 0)}
                      </div>
                      {todayStats?.hasUnvalidated && (
                        <div className="mt-1 text-sm font-medium text-orange-500">
                          {formatCurrency(todayStats?.unvalidatedAmount || 0)} en attente de validation
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium text-gray-600">
                      Chiffre d'affaires validé ce mois
                    </div>
                    <div className="mt-2 text-xl font-bold text-green-600">
                      {formatCurrency(validatedTotal || 0)}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium text-gray-600">
                      Chiffre d'affaires validé {format(subMonths(new Date(), 1), 'MMMM yyyy', { locale: fr })}
                    </div>
                    <div className="mt-2 text-xl font-bold text-green-600">
                      {formatCurrency(lastMonthStats || 0)}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="text-sm font-medium text-gray-600">
                      Chiffre d'affaires validé cette année
                    </div>
                    <div className="mt-2 text-xl font-bold text-green-600">
                      {formatCurrency(yearlyTotal || 0)}
                    </div>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCharts(!showCharts)}
                className="whitespace-nowrap w-full md:w-auto"
              >
                {showCharts ? "Masquer les graphiques" : "Voir les graphiques"}
              </Button>
            </div>

            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center gap-2 w-full">
                  <span className="text-sm font-medium whitespace-nowrap">Du:</span>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex items-center gap-2 w-full">
                  <span className="text-sm font-medium whitespace-nowrap">Au:</span>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="col-span-1 md:col-span-2 bg-white rounded-lg p-4 shadow-sm w-full">
                  <div className="text-sm font-medium text-gray-600">
                    Chiffre d'affaires pour la période
                  </div>
                  <div className="mt-2 text-xl font-bold text-green-600">
                    {formatCurrency(customPeriodTotal || 0)}
                  </div>
                </div>
              </div>
            </div>

            {showCharts && (
              <StatisticsCharts
                validatedTotal={validatedTotal || 0}
                lastMonthStats={lastMonthStats || 0}
                yearlyTotal={yearlyTotal || 0}
                customPeriodTotal={customPeriodTotal || 0}
                startDate={startDate}
                endDate={endDate}
              />
            )}
          </div>
        </Card>

        <Card className="p-2 md:p-6">
          {viewMode === "table" ? (
            <TransactionTable transactionType={transactionType} period={period} selectedDate={selectedDate} />
          ) : (
            transactions && <TransactionCharts transactions={transactions} />
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default History;
